import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toast } from 'react-toastify'
import { baseUrl } from 'utils/axios'
import { IRoleTemplate, IRoleTemplateSlice } from 'types/roles.interface'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { INameId } from 'constants/globalTypes'
import {
  ISingleRoleTemplateGetResponse,
  ISingleRoleTemplatePostRequest,
} from 'types/api/roleTemplates.interface'
import { IPermissionPolicy } from 'types/api/clientRoles.interface'

const initialState: IRoleTemplateSlice = {
  roleTemplates: [],
  isLoading: false,
  singleRoleTemplate: {
    id: null,
    name: '',
    permissionPolicy: null,
  },
  totalCount: 0,
  totalPages: 0,
  noRoleTemplates: false,
  permissionPolicyTemplates: {
    data: [],
    totalPages: 0,
    totalCount: 0,
  },
  singlePermissionPolicyTemplate: null,
}

export const getRoleTemplates = createAsyncThunk(
  'adminRoles/getRoles',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<INameId>> = await baseUrl.get(
        '/roles/template/all',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleRoleTemplate = createAsyncThunk(
  'adminRoles/getSingleRole',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<ISingleRoleTemplateGetResponse> = await baseUrl.get(
        'roles/template/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addRoleTemplate = createAsyncThunk(
  'adminRoles/addRole',
  async (role: IRoleTemplate, thunkAPI) => {
    try {
      const req: ISingleRoleTemplatePostRequest = {
        name: role.name,
        permissionPolicyId: role.permissionPolicy.value,
      }
      const resp = await baseUrl.post('/roles/template', req, thunkAPI)
      return { id: resp.data.id, name: role.name }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteRoleTemplate = createAsyncThunk(
  'adminRoles/deleteRole',
  async (id: string | number, thunkAPI) => {
    try {
      try {
        await baseUrl.delete('roles/template/' + id, thunkAPI)
        return id
      } catch (error) {
        return error
      }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
export const updateRoleTemplate = createAsyncThunk(
  'adminRoles/updateRole',
  async (role: IRoleTemplate, thunkAPI) => {
    try {
      const req: ISingleRoleTemplatePostRequest = {
        name: role.name,
        permissionPolicyId: role.permissionPolicy.value,
      }
      await baseUrl.patch(`roles/template/${role.id}`, req, thunkAPI)
      return { id: role.id, name: role.name }
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteRolePermissionPolicyTemplate = createAsyncThunk(
  'clientRoles/deleteRolePermissionPolicyTemplate',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('permission-policy/role-template/' + id)
      await thunkAPI.dispatch(getRolePermissionPolicyTemplates(getParams({ location })))
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getRolePermissionPolicyTemplates = createAsyncThunk(
  'clientRoles/getRolePermissionPolicyTemplates',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IPermissionPolicy>> = await baseUrl.get(
        'permission-policy/role-template',
        {
          params,
        },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleRolePermissionPolicyTemplate = createAsyncThunk(
  'clientRoles/getSingleRolePermissionPolicy',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IPermissionPolicy> = await baseUrl.get(
        'permission-policy/role-template/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const addRolePermissionPolicyTemplate = createAsyncThunk(
  'clientRoles/addRolePermissionPolicyTemplate',
  async (state: Omit<IPermissionPolicy, 'id'>, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.post('permission-policy/role-template', state)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editRolePermissionPolicyTemplate = createAsyncThunk(
  'clientRoles/editRolePermissionPolicyTemplate',
  async (state: IPermissionPolicy, thunkAPI) => {
    try {
      const resp: AxiosResponse = await baseUrl.patch(
        'permission-policy/role-template/' + state.id,
        state,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const roleTemplatesSlice = createSlice({
  name: 'roleTemplates',
  initialState,
  reducers: {
    clearRoleTemplate: (state) => {
      state.singleRoleTemplate = {
        name: '',
        id: null,
        permissionPolicy: null,
      }
    },
    clearRoleTemplatesList: (state) => {
      state.roleTemplates = []
      state.totalCount = null
      state.totalPages = null
    },
    clearRolePermissionPolicyTemplatesList: (state) => {
      state.permissionPolicyTemplates = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
    clearSinglePermissionPolicyTemplate: (state) => {
      state.singlePermissionPolicyTemplate = null
    },
  },
  extraReducers: {
    [getRoleTemplates.pending.type]: (state) => {
      state.isLoading = true
    },
    [getRoleTemplates.fulfilled.type]: (
      state: IRoleTemplateSlice,
      { payload }: { payload: IPaginatedResponse<INameId> },
    ) => {
      state.roleTemplates = payload.data
      state.isLoading = false
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.noRoleTemplates = state.roleTemplates.length < 1
    },
    [getRoleTemplates.rejected.type]: (state) => {
      state.isLoading = false
    },
    [deleteRoleTemplate.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.roleTemplates = state.roleTemplates.filter((role) => role.id !== payload)
      toast.success('Role template deleted')
    },
    [addRoleTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [addRoleTemplate.fulfilled.type]: (
      state: IRoleTemplateSlice,
      { payload }: { payload: INameId },
    ) => {
      if (state.roleTemplates.length < 30) {
        state.roleTemplates = [...state.roleTemplates, payload]
      }
      toast.success('Role template added')
    },
    [addRoleTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleRoleTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleRoleTemplate.fulfilled.type]: (
      state: IRoleTemplateSlice,
      { payload }: { payload: ISingleRoleTemplateGetResponse },
    ) => {
      state.isLoading = false
      state.singleRoleTemplate = {
        name: payload.name,
        id: payload.id,
        permissionPolicy: {
          label: payload.permissionPolicy.policyName,
          value: payload.permissionPolicy.id,
        },
      }
    },
    [getSingleRoleTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [updateRoleTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [updateRoleTemplate.fulfilled.type]: (
      state: IRoleTemplateSlice,
      { payload }: { payload: INameId },
    ) => {
      state.isLoading = false
      const foundIndex = state.roleTemplates.findIndex((item) => item.id === payload.id)
      state.roleTemplates[foundIndex] = payload
      toast.success('Role template updated')
    },
    [updateRoleTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteRolePermissionPolicyTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteRolePermissionPolicyTemplate.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Permission policy template successfully deleted')
    },
    [deleteRolePermissionPolicyTemplate.rejected.type]: (state, { payload }) => {
      handleErrors(payload)
      state.isLoading = false
    },
    [addRolePermissionPolicyTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [addRolePermissionPolicyTemplate.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Role permission policy successfully added')
    },
    [addRolePermissionPolicyTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleRolePermissionPolicyTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleRolePermissionPolicyTemplate.fulfilled.type]: (
      state,
      { payload }: { payload: IPermissionPolicy },
    ) => {
      state.isLoading = false
      state.singlePermissionPolicyTemplate = payload
    },
    [getSingleRolePermissionPolicyTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editRolePermissionPolicyTemplate.pending.type]: (state) => {
      state.isLoading = true
    },
    [editRolePermissionPolicyTemplate.fulfilled.type]: (state) => {
      state.isLoading = false
      toast.success('Role permission policy template successfully updated')
    },
    [editRolePermissionPolicyTemplate.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getRolePermissionPolicyTemplates.pending.type]: (state) => {
      state.isLoading = true
    },
    [getRolePermissionPolicyTemplates.fulfilled.type]: (state, { payload }) => {
      state.isLoading = false
      state.permissionPolicyTemplates = payload
    },
    [getRolePermissionPolicyTemplates.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const {
  clearRoleTemplate,
  clearRoleTemplatesList,
  clearRolePermissionPolicyTemplatesList,
  clearSinglePermissionPolicyTemplate,
} = roleTemplatesSlice.actions

export default roleTemplatesSlice.reducer
